import { ENTER_DUR, HOLD_DUR, R_CORRIDOR, R_PLANET, R_SPAWN } from '../constants';
import { angleDelta, angleDist, ease, lerp, lerpAngle, norm } from '../polar';
import { randSign } from '../rng';
import type { Content, Enemy, World } from '../types';
import { slotRadius, slotTheta } from '../world';
import { coreImpact, damageEnemy, thaw } from './damage';
import { spawnChildAt } from './spawning';

/**
 * The three-beat entrance (§6.1) plus the attack run. Everything here is polar:
 * staging interpolates (theta, r) from the corridor entry to the spawn ring,
 * which is what makes a flank approach curve through a corner for free.
 */
export function updateEnemies(world: World, content: Content, dt: number): void {
  for (const e of world.enemies) {
    if (!e.alive) continue;

    tickStatus(world, content, e, dt);
    if (!e.alive) continue;

    e.phaseT += dt;

    switch (e.phase) {
      case 'enter':
        stepEnter(e);
        break;
      case 'stage':
        stepStage(e);
        break;
      case 'hold':
        stepHold(world, e);
        break;
      case 'attack':
        stepAttack(world, content, e, dt);
        break;
      case 'park':
        stepPark(world, content, e, dt);
        break;
      default:
        break;
    }
  }
}

function tickStatus(world: World, content: Content, e: Enemy, dt: number): void {
  if (e.burnT > 0) {
    e.burnT -= dt;
    damageEnemy(world, content, e, e.burnDps * dt, null);
    if (!e.alive) return;
  }
  if (e.stunT > 0) e.stunT -= dt;
  if (e.slowT > 0) {
    e.slowT -= dt;
    if (e.slowT <= 0) e.slowMult = 1;
  }
  if (e.freezeT > 0) {
    e.freezeT -= dt;
    if (e.freezeT <= 0) thaw(world, content, e);
  }
}

/** Frozen and stunned targets do not move; slows scale the attack run. */
function motionScale(e: Enemy): number {
  if (e.freezeT > 0 || e.stunT > 0) return 0;
  return e.slowMult;
}

function stepEnter(e: Enemy): void {
  const t = ease(e.phaseT / ENTER_DUR);
  e.r = lerp(e.phaseR, R_CORRIDOR, t);
  e.theta = e.entryTheta;
  if (e.phaseT >= ENTER_DUR) {
    e.phase = 'stage';
    e.phaseT = 0;
    e.phaseR = e.r;
    e.phaseTheta = e.theta;
  }
}

function stepStage(e: Enemy): void {
  const t = ease(e.phaseT / e.stageDur);
  e.r = lerp(e.phaseR, R_SPAWN, t);
  e.theta = lerpAngle(e.phaseTheta, e.bearing, t);
  if (e.phaseT >= e.stageDur) {
    e.phase = 'hold';
    e.phaseT = 0;
    e.r = R_SPAWN;
    e.theta = e.bearing;
  }
}

function stepHold(world: World, e: Enemy): void {
  if (e.phaseT >= HOLD_DUR) {
    e.phase = 'attack';
    e.phaseT = 0;
    e.phaseR = e.r;
  }
  void world;
}

function stepAttack(world: World, content: Content, e: Enemy, dt: number): void {
  const scale = motionScale(e);
  e.r -= e.speed * scale * dt;

  switch (e.def.behaviour) {
    case 'feint': {
      // Telegraphs bearing A the whole way down, then commits to B late.
      const veerAt = e.def.veerAt ?? 250;
      if (!e.veered && e.r <= veerAt) {
        e.veered = true;
        const spread = e.def.veerSpread ?? 1.1;
        e.bearing = norm(e.bearing + spread * randSign(world.rng));
      }
      if (e.veered) {
        // Snap across quickly — the punish for over-committing the ring.
        const d = angleDelta(e.theta, e.bearing);
        const step = Math.min(Math.abs(d), 2.4 * dt) * Math.sign(d);
        e.theta = norm(e.theta + step);
      }
      break;
    }
    case 'burrow': {
      // Dives beneath the orbit ring and stays there — ring guns lose it.
      if (!e.underRing && e.r <= world.ringR + 6) e.underRing = true;
      break;
    }
    case 'carrier': {
      const parkR = e.def.parkR ?? 250;
      if (e.r <= parkR) {
        e.r = parkR;
        e.phase = 'park';
        e.phaseT = 0;
        e.timer = 0;
      }
      break;
    }
    case 'healer': {
      // Patches up everything on nearby bearings. Ignore it and the wave stops
      // dying; focus it and the wave falls over.
      e.timer += dt;
      if (e.timer >= 0.25) {
        e.timer = 0;
        const arc = e.def.healArc ?? 0.7;
        const amount = (e.def.healRate ?? 12) * 0.25;
        for (const other of world.enemies) {
          if (!other.alive || other === e) continue;
          if (other.phase === 'enter' || other.phase === 'dead') continue;
          if (angleDist(other.theta, e.theta) > arc) continue;
          other.hp = Math.min(other.maxHp, other.hp + amount);
        }
      }
      break;
    }

    case 'boss': {
      // Multi-phase: it slows and hardens as it closes, and shoves the ring.
      const frac = e.hp / e.maxHp;
      const phase = frac > 0.66 ? 0 : frac > 0.33 ? 1 : 2;
      if (phase !== e.phaseIndex) {
        e.phaseIndex = phase;
        // Attacks the ring itself: a hard shove on transition.
        world.ringVel += 1.8 * randSign(world.rng);
      }
      e.speed = e.def.speed * (1 - phase * 0.22);
      break;
    }
    default:
      break;
  }

  if (e.r <= R_PLANET + e.def.size * 0.5) {
    coreImpact(world, content, e);
  }
}

function stepPark(world: World, content: Content, e: Enemy, dt: number): void {
  if (motionScale(e) === 0) return;
  e.timer += dt;
  const every = e.def.spawnEvery ?? 2.5;
  if (e.timer >= every) {
    e.timer -= every;
    const child = e.def.spawns;
    if (child) spawnChildAt(world, content, child, e.theta, e.r, e.underRing);
  }
}

/** Wraiths are only visible inside a radar structure's coverage. */
export function updateCloaks(world: World, content: Content): void {
  const radars: Array<{ theta: number; r: number; range: number }> = [];
  for (const slot of world.slots) {
    const st = slot.structure;
    if (!st) continue;
    const def = content.structures.get(st.defId);
    if (!def?.radar) continue;
    const tier = def.tiers[st.tier];
    if (!tier) continue;
    radars.push({
      theta: slotTheta(world, slot),
      r: slotRadius(world, slot),
      range: (def.tiers[st.tier]?.range ?? 0) + 60,
    });
  }

  for (const e of world.enemies) {
    if (!e.alive || !e.cloaked) continue;
    e.revealed = false;
    for (const rad of radars) {
      if (Math.abs(e.r - rad.r) <= rad.range) {
        e.revealed = true;
        break;
      }
    }
  }
}
